import { AxiosResponse } from 'axios';

import {
    ConsumptionPresetsRequestMessage,
    ConsumptionPresetsResponseMessage,
} from '../../../../types/ConsumptionPresetsRequestMessage';
import {
    ReviewUnmatchedProductsRequest,
    UnmatchedVesselProductsResponse,
} from '../../../../types/UnmatchedVesselProductMessage';
import { VesselCreateRequestMessage } from '../../../../types/VesselCreateRequestMessage';
import { VesselMessage } from '../../../../types/VesselMessage';
import { VesselMigrationRequestMessage, VesselMigrationResultMessage } from '../../../../types/VesselMigrationMessage';
import { VesselProduct } from '../../../../types/VesselProduct';
import { VesselProductMessage } from '../../../../types/VesselProductMessage';
import { authorizedAxiosInstance } from '../instances/AuthorizedAxiosInstance';
import { nonEmpty } from '../paramUtils';

interface UpdateVesselParams {
    vesselId: string;
    vessel: unknown;
}

export const updateVessel = ({ vesselId, vessel }: UpdateVesselParams): Promise<AxiosResponse> => {
    return authorizedAxiosInstance.put(`/v1/vessel/${vesselId}`, vessel);
};

interface CreateVesselParams {
    vessel: VesselCreateRequestMessage;
}

export const createVessel = ({ vessel }: CreateVesselParams): Promise<VesselMessage> => {
    return authorizedAxiosInstance.post('/v1/vessel', vessel);
};

interface CreateLubricationChartParams {
    vesselId: string;
    file: File;
}

export const createLubricationChart = ({ vesselId, file }: CreateLubricationChartParams): Promise<AxiosResponse> => {
    const formData = new FormData();
    formData.append('file', file);

    return authorizedAxiosInstance.post(`/v1/vessel/${vesselId}/lubrication-chart`, formData, {
        headers: {
            'content-type': 'multipart/form-data',
        },
    });
};

export interface GetVesselListParams {
    searchQuery?: string;
    customerIds?: string[] | string;
    active?: boolean;
    imo?: string;
}

export const getVesselList = async ({
    searchQuery,
    customerIds,
    active,
    imo,
}: GetVesselListParams): Promise<VesselMessage[]> => {
    const response = await authorizedAxiosInstance.get(`/v1/vessel/list`, {
        params: {
            active: active ?? undefined,
            searchQuery: searchQuery?.trim() || undefined,
            customerIds: nonEmpty(customerIds),
            imo: imo || undefined,
        },
    });
    return response.data;
};

export const deleteLubricationChart = (vesselId: string): Promise<AxiosResponse> => {
    return authorizedAxiosInstance.delete(`/v1/vessel/${vesselId}/lubrication-chart`);
};

export const getConsumptionPresets = async (vesselId: string): Promise<ConsumptionPresetsResponseMessage> => {
    const response = await authorizedAxiosInstance.get(`/v2/vessels/${vesselId}/presets`);
    return response.data;
};

interface UpdatePresetsParams {
    vesselId: string;
    presets: ConsumptionPresetsRequestMessage;
}

export const updateConsumptionPresets = async ({
    vesselId,
    presets,
}: UpdatePresetsParams): Promise<ConsumptionPresetsResponseMessage> => {
    return authorizedAxiosInstance.put(`/v2/vessels/${vesselId}/presets`, presets);
};

export const downloadVesselProductsForCustomer = (customerId: string): Promise<AxiosResponse<Blob>> => {
    return authorizedAxiosInstance.get(`/v2/vessels/products/export`, {
        responseType: 'blob',
        params: {
            customerId,
        },
    });
};

export const downloadVesselList = (): Promise<AxiosResponse<Blob>> => {
    return authorizedAxiosInstance.get('/v2/vessels/export', {
        responseType: 'blob',
    });
};

export const downloadVesselProductsForVessel = (vesselId: string): Promise<AxiosResponse<Blob>> => {
    return authorizedAxiosInstance.get(`/v2/vessels/${vesselId}/products/export`, {
        responseType: 'blob',
    });
};

export const deleteVesselProduct = async (vesselId: string, productId: string): Promise<void> => {
    await authorizedAxiosInstance.delete(`/v2/vessels/${vesselId}/products/${productId}`);
};

export const deleteVesselProducts = async (vesselId: string, productIds: string[]): Promise<void> => {
    await authorizedAxiosInstance.delete(`/v2/vessels/${vesselId}/products`, {
        data: { productIds },
    });
};

interface UpdateVesselProductParams {
    vesselId: string;
    vesselProductId: string;
    vesselProduct: VesselProduct;
}

export const updateVesselProduct = async ({
    vesselId,
    vesselProductId,
    vesselProduct,
}: UpdateVesselProductParams): Promise<VesselProductMessage> => {
    const response = await authorizedAxiosInstance.put(
        `/v2/vessels/${vesselId}/products/${vesselProductId}`,
        vesselProduct
    );
    return response.data;
};

interface CreateVesselProductParams {
    vesselId: string;
    vesselProduct: VesselProduct;
}

export const createVesselProduct = async ({
    vesselId,
    vesselProduct,
}: CreateVesselProductParams): Promise<VesselProductMessage> => {
    const response = await authorizedAxiosInstance.post(`/v2/vessels/${vesselId}/products`, vesselProduct);
    return response.data;
};

export const getUnmatchedVesselProducts = async (vesselId: string): Promise<UnmatchedVesselProductsResponse> => {
    const response = await authorizedAxiosInstance.get(`/v2/vessels/${vesselId}/unmatched-products`);
    return response.data;
};

interface ReviewUnmatchedVesselProductsParams {
    vesselId: string;
    request: ReviewUnmatchedProductsRequest;
}

export const reviewUnmatchedVesselProducts = async ({
    vesselId,
    request,
}: ReviewUnmatchedVesselProductsParams): Promise<void> => {
    await authorizedAxiosInstance.post(`/v2/vessels/${vesselId}/unmatched-products/review`, request);
};

interface MigrateVesselDataParams {
    sourceVesselId: string;
    request: VesselMigrationRequestMessage;
}

export const migrateVesselData = async ({
    sourceVesselId,
    request,
}: MigrateVesselDataParams): Promise<VesselMigrationResultMessage> => {
    const response = await authorizedAxiosInstance.post(`/v2/vessels/${sourceVesselId}/migration`, request);
    return response.data;
};
